import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  X, Loader2, Shield, ShieldOff, Search, Download, RefreshCw,
  Radio, Clock, Users, AlertTriangle, Info, AlertCircle, ToggleLeft, ToggleRight,
  ChevronDown, ChevronRight, Copy, Check,
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Badge } from "./components/ui/badge";
import { toast } from "sonner";
import { API } from "./apiConfig";

const SEVERITY_COLORS = {
  info: "bg-blue-50 text-blue-700 border-blue-200",
  warn: "bg-amber-50 text-amber-700 border-amber-200",
  error: "bg-red-50 text-red-700 border-red-200",
};
const SEVERITY_ICONS = {
  info: <Info size={12} />,
  warn: <AlertTriangle size={12} />,
  error: <AlertCircle size={12} />,
};

export function CloverBridge({ isOpen, onClose }) {
  const [tab, setTab] = useState("merchants");
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="clover-bridge">
      <div className="bg-slate-50 rounded-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col shadow-xl">
        <div className="bg-[#0A1F3B] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Radio size={18} className="text-[#F5D300]" />
            <h2 className="text-white font-semibold text-base">Clover Bridge</h2>
          </div>
          <div className="flex items-center gap-2">
            {["merchants", "shifts", "events"].map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${
                  tab === t ? "bg-[#F5D300] text-black" : "text-white/60 hover:text-white"
                }`}
                data-testid={`bridge-tab-${t}`}
              >
                {t === "merchants" ? "Merchants" : t === "shifts" ? "Shifts" : "Event Log"}
              </button>
            ))}
            <button onClick={onClose} className="text-white/60 hover:text-white ml-4" data-testid="bridge-close">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {tab === "merchants" && <MerchantsTab />}
          {tab === "shifts" && <ShiftsTab />}
          {tab === "events" && <EventLogTab />}
        </div>
      </div>
    </div>
  );
}

// ─── Verification Code Card ──────────────────────────────────────────

function VerificationCodeCard() {
  const [code, setCode] = useState(null);
  const [receivedAt, setReceivedAt] = useState(null);
  const [copied, setCopied] = useState(false);
  const intervalRef = useRef(null);

  const fetchCode = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/payroll/admin/clover/last-verification-code`, { withCredentials: true });
      setCode(data.code || null);
      setReceivedAt(data.received_at || null);
    } catch {
      // silently fail — admin might not be logged in
    }
  }, []);

  useEffect(() => {
    fetchCode();
    intervalRef.current = setInterval(fetchCode, 3000);
    return () => clearInterval(intervalRef.current);
  }, [fetchCode]);

  const handleCopy = () => {
    if (code) {
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success("Copied to clipboard");
    }
  };

  return (
    <div className="bg-white border border-[#F5D300]/40 rounded-lg p-4" data-testid="verification-code-card">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#0A1F3B]">Latest Clover Verification Code</h3>
        <span className="text-[9px] text-slate-400">Polls every 3s</span>
      </div>
      {code ? (
        <div className="flex items-center gap-3 mt-2">
          <code className="text-2xl font-mono font-black text-[#0A1F3B] tracking-widest bg-slate-50 border border-slate-200 rounded-lg px-4 py-2 select-all" data-testid="verification-code-value">
            {code}
          </code>
          <Button size="sm" variant="outline" onClick={handleCopy} className="h-8 text-xs gap-1" data-testid="copy-code-btn">
            {copied ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
            {copied ? "Copied" : "Copy"}
          </Button>
          <span className="text-[10px] text-slate-500">
            Received: {receivedAt ? new Date(receivedAt).toLocaleString() : "—"}
          </span>
        </div>
      ) : (
        <p className="text-xs text-slate-500 mt-1">
          No code received yet. Click <span className="font-semibold">SEND VERIFICATION CODE</span> in the Clover Dashboard.
        </p>
      )}
    </div>
  );
}

// ─── Merchants Tab ───────────────────────────────────────────────────

function MerchantsTab() {
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/payroll/admin/merchants`, { withCredentials: true });
      setMerchants(data.merchants || []);
    } catch (err) {
      toast.error("Failed to load merchants", { description: err.response?.data?.detail || err.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleKill = async (mid, currentEnabled) => {
    try {
      await axios.post(`${API}/payroll/admin/merchants/${mid}/kill-switch`,
        { app_enabled: !currentEnabled }, { withCredentials: true });
      toast.success(`${mid}: ${!currentEnabled ? "Enabled" : "Disabled"}`);
      load();
    } catch (err) {
      toast.error("Failed", { description: err.response?.data?.detail || err.message });
    }
  };

  const toggleDev = async (mid, currentDev) => {
    try {
      await axios.post(`${API}/payroll/admin/merchants/${mid}/dev-flag`,
        { is_dev_merchant: !currentDev }, { withCredentials: true });
      toast.success(`Dev flag ${!currentDev ? "ON" : "OFF"}`);
      load();
    } catch (err) {
      toast.error("Failed");
    }
  };

  const updateSync = async (mid, field, value) => {
    const merchant = merchants.find(m => m.clover_merchant_id === mid);
    if (!merchant) return;
    const body = {
      auto_sync_enabled: merchant.auto_sync_enabled,
      auto_sync_cadence: merchant.auto_sync_cadence,
      sync_format: merchant.sync_format,
      [field]: value,
    };
    try {
      await axios.post(`${API}/payroll/admin/merchants/${mid}/auto-sync`, body, { withCredentials: true });
      toast.success("Sync settings updated");
      load();
    } catch (err) {
      toast.error("Failed");
    }
  };

  const filtered = search
    ? merchants.filter(m => m.clover_merchant_id.toLowerCase().includes(search.toLowerCase()) || (m.merchant_name || "").toLowerCase().includes(search.toLowerCase()))
    : merchants;

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin text-[#0A1F3B]" size={24} /></div>;

  return (
    <div className="space-y-4">
      <VerificationCodeCard />
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Search merchant ID..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9 h-8 text-xs" data-testid="merchant-search" />
        </div>
        <Button size="sm" variant="outline" onClick={load} className="h-8 text-xs gap-1">
          <RefreshCw size={12} /> Refresh
        </Button>
        <span className="text-xs text-slate-500">{filtered.length} merchants</span>
      </div>

      {filtered.map((m) => {
        const isExp = expanded.has(m.clover_merchant_id);
        return (
          <div key={m.clover_merchant_id} className={`border rounded-lg overflow-hidden ${m.app_enabled ? "border-slate-200" : "border-red-200 bg-red-50/30"}`}
               data-testid={`merchant-${m.clover_merchant_id}`}>
            <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50/50"
                 onClick={() => setExpanded(prev => { const n = new Set(prev); isExp ? n.delete(m.clover_merchant_id) : n.add(m.clover_merchant_id); return n; })}>
              {isExp ? <ChevronDown size={14} className="text-slate-500" /> : <ChevronRight size={14} className="text-slate-500" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-mono font-semibold text-[#0A1F3B]">{m.clover_merchant_id}</span>
                  {m.merchant_name && <span className="text-xs text-slate-500">{m.merchant_name}</span>}
                  {m.is_dev_merchant && <Badge className="bg-violet-100 text-violet-700 text-[10px] h-4">DEV</Badge>}
                  {!m.app_enabled && <Badge className="bg-red-100 text-red-700 text-[10px] h-4">DISABLED</Badge>}
                  {m.app_enabled && <Badge className="bg-green-100 text-green-700 text-[10px] h-4">ACTIVE</Badge>}
                </div>
                <div className="flex items-center gap-3 mt-0.5 text-[10px] text-slate-500">
                  <span>Installed: {m.installed_at ? new Date(m.installed_at).toLocaleDateString() : "—"}</span>
                  <span>Last sync: {m.last_synced_at ? new Date(m.last_synced_at).toLocaleString() : "never"}</span>
                  {m.auto_sync_enabled && <span className="text-blue-600">Auto-sync: {m.auto_sync_cadence}</span>}
                </div>
              </div>
              <button onClick={(e) => { e.stopPropagation(); toggleKill(m.clover_merchant_id, m.app_enabled); }}
                      className={`p-1.5 rounded transition-colors ${m.app_enabled ? "text-green-600 hover:bg-green-50" : "text-red-600 hover:bg-red-50"}`}
                      title={m.app_enabled ? "Disable (kill switch)" : "Enable"} data-testid={`kill-${m.clover_merchant_id}`}>
                {m.app_enabled ? <Shield size={16} /> : <ShieldOff size={16} />}
              </button>
            </div>

            {isExp && (
              <div className="border-t border-slate-100 px-4 py-3 space-y-3 bg-white">
                <div className="flex items-center gap-4 flex-wrap text-xs">
                  <label className="flex items-center gap-1.5 cursor-pointer" onClick={() => toggleDev(m.clover_merchant_id, m.is_dev_merchant)}>
                    {m.is_dev_merchant ? <ToggleRight size={16} className="text-violet-600" /> : <ToggleLeft size={16} className="text-slate-400" />}
                    Dev merchant
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer" onClick={() => updateSync(m.clover_merchant_id, "auto_sync_enabled", !m.auto_sync_enabled)}>
                    {m.auto_sync_enabled ? <ToggleRight size={16} className="text-blue-600" /> : <ToggleLeft size={16} className="text-slate-400" />}
                    Auto-sync
                  </label>
                  {m.auto_sync_enabled && (
                    <>
                      <select value={m.auto_sync_cadence} onChange={e => updateSync(m.clover_merchant_id, "auto_sync_cadence", e.target.value)}
                              className="h-6 text-[10px] bg-slate-50 border border-slate-200 rounded px-1">
                        <option value="weekly">Weekly</option>
                        <option value="biweekly">Bi-weekly</option>
                        <option value="off">Off</option>
                      </select>
                      <select value={m.sync_format} onChange={e => updateSync(m.clover_merchant_id, "sync_format", e.target.value)}
                              className="h-6 text-[10px] bg-slate-50 border border-slate-200 rounded px-1">
                        <option value="json">JSON</option>
                        <option value="csv">CSV</option>
                      </select>
                    </>
                  )}
                </div>
                {m.message && <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">{m.message}</div>}
                {m.recent_events && m.recent_events.length > 0 && (
                  <div>
                    <p className="text-[10px] font-semibold text-slate-500 mb-1">Recent events:</p>
                    {m.recent_events.map((ev, i) => (
                      <div key={i} className={`flex items-center gap-2 text-[10px] px-2 py-0.5 rounded border mb-0.5 ${SEVERITY_COLORS[ev.severity] || SEVERITY_COLORS.info}`}>
                        {SEVERITY_ICONS[ev.severity] || SEVERITY_ICONS.info}
                        <span className="font-mono">{ev.event_type}</span>
                        <span className="ml-auto text-[9px] opacity-70">{ev.occurred_at ? new Date(ev.occurred_at).toLocaleString() : ""}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {filtered.length === 0 && <p className="text-center text-sm text-slate-500 py-8">No merchants found</p>}
    </div>
  );
}

// ─── Shifts Tab ──────────────────────────────────────────────────────

function ShiftsTab() {
  const [merchantId, setMerchantId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [shifts, setShifts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!merchantId) { toast.error("Enter a merchant ID"); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page) });
      if (startDate) params.set("start", startDate);
      if (endDate) params.set("end", endDate);
      const { data } = await axios.get(`${API}/payroll/admin/merchants/${merchantId}/shifts?${params}`, { withCredentials: true });
      setShifts(data.shifts || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast.error("Failed", { description: err.response?.data?.detail || err.message });
    } finally {
      setLoading(false);
    }
  };

  const downloadCsv = () => {
    const params = new URLSearchParams();
    if (startDate) params.set("start", startDate);
    if (endDate) params.set("end", endDate);
    const url = `${API}/payroll/admin/merchants/${merchantId}/export.csv?${params}`;
    window.open(url, "_blank");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Input placeholder="Merchant ID" value={merchantId} onChange={e => setMerchantId(e.target.value)}
               className="h-8 text-xs w-48" data-testid="shifts-merchant-input" />
        <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="h-8 text-xs w-36" />
        <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="h-8 text-xs w-36" />
        <Button size="sm" onClick={load} disabled={loading} className="bg-[#0A1F3B] text-white h-8 text-xs gap-1">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />} Search
        </Button>
        {merchantId && (
          <Button size="sm" variant="outline" onClick={downloadCsv} className="h-8 text-xs gap-1" data-testid="download-csv-btn">
            <Download size={12} /> Export CSV
          </Button>
        )}
        <span className="text-xs text-slate-500 ml-auto">{total} shifts</span>
      </div>

      {shifts.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 text-slate-500 border-b">
                <th className="text-left px-3 py-2">Employee</th>
                <th className="text-left px-3 py-2">ID</th>
                <th className="text-left px-3 py-2">Clock In</th>
                <th className="text-left px-3 py-2">Clock Out</th>
                <th className="text-right px-3 py-2">Hours</th>
                <th className="text-right px-3 py-2">Tips</th>
                <th className="text-left px-3 py-2">Role</th>
              </tr>
            </thead>
            <tbody>
              {shifts.map((s, i) => {
                const inT = s.in_time ? new Date(s.in_time) : null;
                const outT = s.out_time ? new Date(s.out_time) : null;
                const hours = inT && outT ? ((outT - inT) / 3600000).toFixed(2) : "—";
                const tips = s.cash_tip_amount_cents ? `$${(s.cash_tip_amount_cents / 100).toFixed(2)}` : "—";
                return (
                  <tr key={i} className={`border-b border-slate-50 ${!s.out_time ? "bg-amber-50/50" : ""}`}>
                    <td className="px-3 py-1.5 font-medium text-[#0A1F3B]">{s.employee_name}</td>
                    <td className="px-3 py-1.5 font-mono text-slate-500">{s.employee_id}</td>
                    <td className="px-3 py-1.5 font-mono">{inT ? inT.toLocaleString() : "—"}</td>
                    <td className="px-3 py-1.5 font-mono">{outT ? outT.toLocaleString() : <span className="text-amber-600">missing</span>}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{hours}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{tips}</td>
                    <td className="px-3 py-1.5 text-slate-500">{s.role || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {total > 100 && (
        <div className="flex items-center justify-center gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => { setPage(p => p - 1); load(); }} className="h-7 text-xs">Prev</Button>
          <span className="text-xs text-slate-500">Page {page} of {Math.ceil(total / 100)}</span>
          <Button size="sm" variant="outline" disabled={page * 100 >= total} onClick={() => { setPage(p => p + 1); load(); }} className="h-7 text-xs">Next</Button>
        </div>
      )}

      {shifts.length === 0 && !loading && <p className="text-center text-sm text-slate-500 py-8">No shifts found. Enter a merchant ID and search.</p>}
    </div>
  );
}

// ─── Event Log Tab ───────────────────────────────────────────────────

function EventLogTab() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [merchantFilter, setMerchantFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      // Load events from all merchants via the merchants list
      const { data } = await axios.get(`${API}/payroll/admin/merchants`, { withCredentials: true });
      const allEvents = [];
      for (const m of (data.merchants || [])) {
        if (merchantFilter && !m.clover_merchant_id.toLowerCase().includes(merchantFilter.toLowerCase())) continue;
        const evRes = await axios.get(`${API}/payroll/admin/merchants/${m.clover_merchant_id}/recent-events?limit=50`, { withCredentials: true });
        for (const ev of (evRes.data.events || [])) {
          ev.merchant_id = m.clover_merchant_id;
          allEvents.push(ev);
        }
      }
      allEvents.sort((a, b) => (b.occurred_at || "").localeCompare(a.occurred_at || ""));
      setEvents(allEvents);
    } catch (err) {
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = typeFilter
    ? events.filter(e => e.event_type.toLowerCase().includes(typeFilter.toLowerCase()))
    : events;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <Input placeholder="Filter merchant..." value={merchantFilter} onChange={e => setMerchantFilter(e.target.value)} className="h-8 text-xs w-40" />
        <Input placeholder="Filter event type..." value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="h-8 text-xs w-40" />
        <Button size="sm" variant="outline" onClick={load} disabled={loading} className="h-8 text-xs gap-1">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Reload
        </Button>
        <span className="text-xs text-slate-500 ml-auto">{filtered.length} events</span>
      </div>

      <div className="space-y-1">
        {filtered.map((ev, i) => (
          <div key={i} className={`flex items-start gap-2 text-xs px-3 py-2 rounded border ${SEVERITY_COLORS[ev.severity] || SEVERITY_COLORS.info}`}>
            {SEVERITY_ICONS[ev.severity] || SEVERITY_ICONS.info}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono font-semibold">{ev.event_type}</span>
                <span className="text-[10px] opacity-70">{ev.merchant_id}</span>
                {ev.device_id && <span className="text-[10px] opacity-50">device: {ev.device_id}</span>}
              </div>
              {ev.payload && Object.keys(ev.payload).length > 0 && (
                <pre className="mt-1 text-[10px] opacity-70 whitespace-pre-wrap break-all">{JSON.stringify(ev.payload, null, 1)}</pre>
              )}
            </div>
            <span className="text-[9px] opacity-60 whitespace-nowrap">{ev.occurred_at ? new Date(ev.occurred_at).toLocaleString() : ""}</span>
          </div>
        ))}
        {filtered.length === 0 && !loading && <p className="text-center text-sm text-slate-500 py-8">No events found</p>}
      </div>
    </div>
  );
}
